/* eslint-disable jsx-a11y/anchor-is-valid */
import { supabase } from "../../../app/supabase-client";
import { useState } from "react";
import {
  Button,
  Spinner,
  Form,
  Alert,
} from "react-bootstrap";
import { useForm, FieldValues } from "react-hook-form";
import { RouterPath } from "../../../app/router-path";
import { BlueButton, StyledCard, StyledFloatingLabel, StyledFormControl } from "app/theme";

function AuthForm() {
  const [loading, setLoading] = useState(false);
  const { register, handleSubmit } = useForm();
  const [registerConfirm, setRegisterConfirm] = useState(false);
  const [forgotPasswordConfirm, setForgotPasswordConfirm] = useState(false);

  const [mode, setMode] = useState<"login" | "register" | "forgotPassword">(
    "login"
  );

  const Register = async (userData: FieldValues) => {
    try {
      setLoading(true);
      if (userData.password !== userData.confirmPassword) {
        alert("Password does not match");
        return;
      }
      const { data, error } = await supabase.auth.signUp({
        email: userData.email,
        password: userData.password,
        options: {
          data: {
            user_name: userData.userName,
            user_kind: userData.userKind,
          },
        },
      });
      if (error) {
        alert(error);
        return;
      }
      if (data) setRegisterConfirm(true);
    } catch (error) {
      alert(error);
    } finally {
      setLoading(false);
    }
  };

  const Login = async (userData: FieldValues) => {
    try {
      setLoading(true);
      const { error } = await supabase.auth.signInWithPassword({
        email: userData.email,
        password: userData.password,
      });
      if (error) {
        alert(error);
        return;
      }
    } catch (error) {
      alert(error);
    } finally {
      setLoading(false);
    }
  };

  const ForgotPassword = async (userData: FieldValues) => {
    try {
      setLoading(true);
      const { data, error } = await supabase.auth.resetPasswordForEmail(
        userData.email,
        { redirectTo: process.env.REACT_APP_BASE_URL + RouterPath.passwordRecovery }
      );
      if (error) {
        alert(error);
        return;
      }
      if (data) setForgotPasswordConfirm(true);
    } catch (error) {
      alert(error);
    } finally {
      setLoading(false);
    }
  };

  return (
    <StyledCard style={{
        width: "40rem",
        padding: "10px",
    }}>
      <StyledCard.Header>
        <h3>
          <strong>
            {mode === "register"
              ? "Register"
              : mode === "login"
              ? "Login"
              : mode === "forgotPassword"
              ? "Reset Password"
              : null}
          </strong>
        </h3>
      </StyledCard.Header>
      <StyledCard.Body>
        {loading ? (
          <Button variant="primary" disabled>
            <Spinner
              as="span"
              animation="grow"
              size="sm"
              role="status"
              aria-hidden="true"
            />
            Loading, please wait...
          </Button>
        ) : (
          <Form
            onSubmit={handleSubmit((data) => {
              switch (mode) {
                case "register":
                  Register(data);
                  break;
                case "login":
                  Login(data);
                  break;
                case "forgotPassword":
                  ForgotPassword(data);
                  break;
                default:
                  break;
              }
            })}
          >
            <StyledFloatingLabel label="Email" className="mb-3">
              <StyledFormControl
                id="email"
                className="inputField"
                type="email"
                placeholder="Your email"
                {...register("email", { required: true })}
              />
            </StyledFloatingLabel>

            {mode === "register" ? (
              <>
                <StyledFloatingLabel label="Name" className="mb-3">
                  <StyledFormControl
                    id="userName"
                    className="inputField"
                    placeholder="Your name"
                    {...register("userName", { required: true })}
                  />
                </StyledFloatingLabel>
              </>
            ) : null}

            {mode === "login" || mode === "register" ? (
              <StyledFloatingLabel label="Password" className="mb-3">
                <StyledFormControl
                  id="password"
                  className="inputField"
                  type="password"
                  placeholder="Your password"
                  {...register("password", { required: true })}
                />
              </StyledFloatingLabel>
            ) : null}

            {mode === "register" ? (
              <StyledFloatingLabel label="Confirm Password" className="mb-3">
                <StyledFormControl
                  id="confirmPassword"
                  className="inputField"
                  type="password"
                  placeholder="Confirm your password"
                  {...register("confirmPassword", { required: true })}
                />
              </StyledFloatingLabel>
            ) : null}

            {registerConfirm ? (
              <Alert variant="success">
                <strong>Success!</strong> Please check your email to confirm
                your account.
              </Alert>
            ) : null}
            {forgotPasswordConfirm ? (
              <Alert variant="success">
                <strong>Success!</strong> Please check your email to reset your
                password. <br /> If you don't see the email, please check your spam folder.
              </Alert>
            ) : null}
            <BlueButton type="submit" aria-live="polite">
              {mode === "register"
                ? "Register"
                : mode === "login"
                ? "Login"
                : mode === "forgotPassword"
                ? "Reset Password"
                : null}
            </BlueButton>
            <hr></hr>
            {mode === "register" ? (
              <>
                {/* eslint-disable-next-line jsx-a11y/anchor-is-valid */}
                <a
                  className="text-muted"
                  onClick={() => {
                    setMode("login");
                  }}
                >
                  Already have an account?
                </a>
              </>
            ) : mode === "login" ? (
              // eslint-disable-next-line jsx-a11y/anchor-is-valid
              <>
                <a
                  className="text-muted"
                  onClick={() => {
                    setMode("register");
                  }}
                >
                  No account?
                </a>
                {/* eslint-disable-next-line jsx-a11y/anchor-has-content */}
                <a
                  className="text-muted mx-3"
                  onClick={() => {
                    setMode("forgotPassword");
                  }}
                >
                  Forgot password?
                </a>
              </>
            ) : mode === "forgotPassword" ? (
              // eslint-disable-next-line jsx-a11y/anchor-is-valid
              <a
                className="text-muted"
                onClick={() => {
                  setMode("login");
                }}
              >
                Back
              </a>
            ) : null}
          </Form>
        )}
      </StyledCard.Body>
    </StyledCard>
  );
}

export default AuthForm;